import { Fragment, Component } from 'react';
import PropTypes from 'prop-types';
import { createRefetchContainer, graphql } from 'react-relay/legacy';
import get from 'lodash.get';
import { trackEvent } from 'dibs-tracking';
import { isFeatureRampingVariant } from '../utils/tracking/isFeatureRampingVariant';
import { getUserSessionCountryCode } from 'dibs-regional-info/exports/regionalInfoHelpers';

import { LABEL_CONTACT_LINK } from '../authentication/authConstants';
import { CONTACT_DEALER_FLOW, sharedLoginCheck } from '../authentication/sharedLoginCheck';
import contactDealerLauncher from 'dibs-buyer-layout/exports/contactDealerLauncher';
import { TradeMultiRequestModalLoader } from 'dibs-buyer-layout/exports/TradeMultiRequestModalLoader';
import { getQueryParam } from '../utils/uriUtils';

class SharedContactDealerWrapperComponent extends Component {
    constructor(props) {
        super(props);
        this.state = { shouldLoad: false, isOpen: false };
        this.showMultiRequestModal = this.getShowMultiRequestModal(props);
        this.onModalClose = this.onModalClose.bind(this);
        this.doRefetch = this.doRefetch.bind(this);
        this.onContactClick = this.onContactClick.bind(this);
        this.launchAuthModal = this.launchAuthModal.bind(this);
        this.isContactModalVariant = this.isContactModalVariant.bind(this);
        this.trackSubmitContactDealerRequest = this.trackSubmitContactDealerRequest.bind(this);
    }

    componentDidMount() {
        const queryParams = get(global, 'location.search');
        const modalParam = getQueryParam(queryParams, 'modal');
        if (
            this.props.loadOnMount ||
            (this.props.modalType === 'CONTACT_DEALER' && modalParam === 'contact-dealer') ||
            (this.props.modalType === 'REQUEST_CUSTOMIZATION' &&
                modalParam === 'request-customization')
        ) {
            this.onContactClick(null);
        }
    }
    UNSAFE_componentWillReceiveProps(nextProps) {
        if (!this.props.user && nextProps.user) {
            this.showMultiRequestModal = this.getShowMultiRequestModal(nextProps);
        }
    }
    loadModal() {
        if (this.showMultiRequestModal) {
            this.setState({ shouldLoad: true, isOpen: true });
        }
    }
    doRefetch(refetchVariables) {
        const variables = {
            ...refetchVariables,
            userCountryCode: getUserSessionCountryCode(),
            itemQuantity: this.props?.itemQuantity,
        };
        this.props.relay.refetch(variables);
    }
    onModalClose() {
        if (this.showMultiRequestModal) {
            this.setState({ isOpen: false });
        }
    }
    onContactClick(event) {
        const isContactModalVariant = this.isContactModalVariant();

        if (this.showMultiRequestModal && !isContactModalVariant) {
            this.loadModal();
        } else {
            const {
                relay,
                item,
                selectedSkuId,
                launchOrigin,
                modalType,
                user,
                currency,
                itemQuantity,
            } = this.props;
            trackEvent(
                {
                    category: 'contact dealer',
                    action: 'contact dealer clicked',
                    label: launchOrigin,
                    eventName: 'contact',
                    step_interaction_name: 'entry',
                    interaction_type: 'contact dealer',
                },
                event
            );
            contactDealerLauncher({
                environment: relay.environment,
                itemId: item.serviceId,
                selectedSkuId,
                launchOrigin,
                modalType,
                currency: currency,
                userId: (user && user.serviceId) || null,
                onSuccessfulShippingQuoteRequest: this.doRefetch,
                onSuccessfulContactSellerRequest: () => {
                    if (this.showMultiRequestModal) {
                        this.trackSubmitContactDealerRequest({ isVariant: true });
                    }
                },
                itemQuantity,
            });
        }
    }
    launchAuthModal({ email, linkLocation: launchSource }) {
        sharedLoginCheck({
            authOptions: {
                email,
                flow: CONTACT_DEALER_FLOW,
                ga: { label: `${LABEL_CONTACT_LINK} - ${launchSource}` },
            },
        });
    }
    isContactModalVariant() {
        return isFeatureRampingVariant({ MIN: 0, MAX: 99 });
    }
    getShowMultiRequestModal(props) {
        return (
            (props.modalType === 'CONTACT_DEALER' || props.modalType === 'REQUEST_CUSTOMIZATION') &&
            !!props.user?.isVerifiedTrade
        );
    }
    trackSubmitContactDealerRequest({ isVariant }) {
        trackEvent(
            {
                category: 'feature ramping',
                label: 'contact dealer submitted',
                action: isVariant ? 'VARIANT' : 'CONTROL',
                additional: {
                    cdnFeatureRamp3: `${isVariant ? 'consumerModal' : 'tradeModal'}`,
                },
            },
            null
        );
    }

    render() {
        const { children, item, message, name, launchOrigin, user, modalType, modalContentData } =
            this.props;

        const { shouldLoad, isOpen } = this.state;
        return (
            <Fragment>
                <div data-tn="contact-dealer-wrapper-cta">{children(this.onContactClick)}</div>
                {this.showMultiRequestModal && (
                    <TradeMultiRequestModalLoader
                        load={shouldLoad}
                        itemId={item.serviceId}
                        launchAuth={this.launchAuthModal}
                        linkLocation={launchOrigin}
                        message={message}
                        name={name}
                        isOpen={isOpen}
                        onClose={this.onModalClose}
                        sellerId={item.seller.serviceId}
                        userId={user && user.serviceId}
                        modalType={modalType}
                        modalContentData={modalContentData}
                        onSuccessfulShippingQuoteRequest={this.doRefetch}
                        onSuccessfulContactSellerRequest={() =>
                            this.trackSubmitContactDealerRequest({ isVariant: false })
                        }
                    />
                )}
            </Fragment>
        );
    }
}

SharedContactDealerWrapperComponent.propTypes = {
    children: PropTypes.func,
    item: PropTypes.object.isRequired,
    selectedSkuId: PropTypes.string,
    launchOrigin: PropTypes.string.isRequired,
    loadOnMount: PropTypes.bool,
    message: PropTypes.string,
    modalContentData: PropTypes.object,
    modalType: PropTypes.oneOf([
        'CONTACT_DEALER',
        'REQUEST_CUSTOMIZATION',
        'REQUEST_PRICE',
        'REQUEST_IMAGES',
        'REQUEST_IMAGES_AND_VIDEOS',
        'DESIGN_YOUR_OWN',
    ]),
    name: PropTypes.string,
    relay: PropTypes.object.isRequired,
    user: PropTypes.object,
    currency: PropTypes.string.isRequired,
    itemQuantity: PropTypes.number,
};

SharedContactDealerWrapperComponent.defaultProps = {
    loadOnMount: false,
    modalType: 'CONTACT_DEALER',
};

SharedContactDealerWrapperComponent = SharedContactDealerWrapperComponent;

export const SharedContactDealerWrapper = createRefetchContainer(
    SharedContactDealerWrapperComponent,
    {
        item: graphql`
            fragment SharedContactDealerWrapper_item on Item {
                seller {
                    serviceId
                }
                serviceId
            }
        `,
        user: graphql`
            fragment SharedContactDealerWrapper_user on User {
                serviceId
                isVerifiedTrade
            }
        `,
    },
    graphql`
        query SharedContactDealerWrapperRefetchQuery(
            $userId: String!
            $itemId: String!
            $userZipCode: String = ""
            $userCountryCode: String = ""
            $isClient: Boolean!
            $fetchUser: Boolean!
            $itemQuantity: Int
        ) {
            viewer {
                ...PdpRespSidebarBlurbShipping_viewer
                ...PdpSharedShippingHolidayDelivery_viewer
                ...PdpSharedShippingInfoLink_viewer
                relevantShipping(
                    itemId: $itemId
                    buyerId: $userId
                    quantity: $itemQuantity
                    zipCode: $userZipCode
                    countryCode: $userCountryCode
                ) {
                    quoteRequestStatus
                    shipmentQuote {
                        ...PdpSharedShippingInfoLink_shipmentQuote
                        handlingTimeDisplay(displayShort: true)
                        subTotalAmount {
                            convertedAmountList {
                                currency
                                amount
                            }
                        }
                        buyerServiceMethodDisplay
                        showQuoteAsPending
                        serviceMethod {
                            serviceType
                            serviceMethodType
                        }
                    }
                    shipment {
                        shippingAddress {
                            displayCityStateCountry
                        }
                    }
                }
            }
        }
    `
);
